import React, { useState } from "react";
import { Bar, BarChart, CartesianGrid, Rectangle, XAxis, YAxis } from "recharts";
import { Card, CardContent } from "../../components/ui/card";
import { ChartConfig, ChartContainer, ChartTooltip } from "../../components/ui/chart";
import { P3Desk } from "../../styles/styles_text";
import NewFilter from "../../assets/kinologyIcons/svg/new_filter.svg";
import WithFilter from "../../assets/kinologyIcons/svg/with_filter.svg";
import Scheduler from "../../assets/kinologyIcons/svg/schedule.svg";
import Filter from "./Filter";
import SearchUsers from "./SearchUsers";
import EmptyComponent from "./EmptyComponent";

interface LabelsTypes {
	label: string;
	color: string;
}

interface DonutChartProps {
	title: string;
	subtitle?: string;
	labels: Array<LabelsTypes>;
	chartData: any;
	onStackedChange: (selected: string[]) => void;
	defaultFilter: any,
	hiddenFilter?: boolean;
}

const StackedBarChart: React.FC<DonutChartProps> = ({
	title,
	subtitle,
	labels,
	chartData,
	onStackedChange,
	defaultFilter,
	hiddenFilter = false,
}) => {
	const chartConfig = {
		desktop: {
			label: "Desktop",
			color: "hsl(var(--chart-1))",
		},
		mobile: {
			label: "Mobile",
			color: "hsl(var(--chart-2))",
		},
	} satisfies ChartConfig;

	const [isTooltipVisible, setIsTooltipVisible] = useState(false);
	const [with_filter, set_with_filter] = useState(false);
	const [activeIndex, setActiveIndex]: any = useState(null);

	const onFilterChange = (selected: any) => {
		const aux_filter = Object.entries(selected)
		set_with_filter(aux_filter.length > 0 ? true : false)
		onStackedChange(selected)
	};

	function _renderLabel(label: string, color: string) {
		return (
			<div
				style={{
					display: "flex",
					flexDirection: "row",
					alignItems: "center",
					gap: "0.5rem",
					marginRight: "1rem",
				}}
			>
				<div
					style={{
						width: "1rem",
						height: "1rem",
						backgroundColor: color,
						borderRadius: "5px",
					}}
				></div>
				<P3Desk style={{ textAlign: "center" }}>{label}</P3Desk>
			</div>
		);
	}

	return (
		<Card
			style={{
				padding: "1rem",
				display: "flex",
				flexDirection: "column",
				backgroundColor: "#FFFFFF",
				width: "100%",
				marginBottom: "0.75rem",
				marginTop: "0.75rem",
				position: "relative",
			}}
		>
			<div style={{ display: hiddenFilter ? 'none' : 'flex', position: "absolute", top: "1rem", right: "1rem" }}>
				<Filter icon={with_filter ? WithFilter : NewFilter} onFilterChange={onFilterChange} defaultFilter={defaultFilter} GraphType={title} Cleanedfilter={()=> set_with_filter(false)} />
			</div>
			<P3Desk style={{ textAlign: "center" }}>{title}</P3Desk>
			{subtitle &&
				<div style={{ width: '100%', display: 'flex', flexDirection: 'row', justifyContent: 'center', margin: '0.3rem' }}>
					<img src={Scheduler} alt="" style={{ marginRight: '0.5rem' }} />
					<P3Desk>
						{subtitle}
					</P3Desk>
				</div>
			}
			<div
				style={{
					width: "100%",
					display: "flex",
					flexDirection: "row",
					justifyContent: "center",
					marginBottom: "5rem",
				}}
			>
				{labels.map((label) => _renderLabel(label.label, label.color))}
			</div>
			{
				chartData?.length > 0
				?
					<CardContent>
						<ChartContainer
							config={chartConfig}
							style={{ height: "450px", width: "100%" }}
							onMouseLeave={() => setIsTooltipVisible(false)}
						>
							<BarChart
								barSize={10}
								data={chartData}
								height={250}
								width={undefined}
							>
								<CartesianGrid vertical={false} strokeDasharray="5 5" />
								<XAxis
									dataKey="eixo_x"
									tickLine={false}
									tickMargin={10}
									angle={-90}
									textAnchor="end"
									height={150}
									tick={({ x, y, payload }) => {
										return (
											<g transform={`translate(${x},${y})`}>
												<text
													x={0}
													y={0}
													dy={3} // deslocamento vertical
													textAnchor="end"
													transform="rotate(-90)"
													style={{ fontSize: 10, fill: '#000' }} // Altere o tamanho da fonte aqui
												>
													{payload.value}
												</text>
											</g>
										);
									}}

								/>
								<YAxis tickLine={false} tickMargin={10} axisLine={false} />

								<ChartTooltip
									cursor={{ fill: 'rgba(0, 0, 0, 0)' }}
									active={isTooltipVisible}
									content={<SearchUsers />}
									trigger={"click"}
								/>
								<Bar
									dataKey="alto_risco_1"
									stackId="a"
									fill="#E57E00"
									onClick={() => setIsTooltipVisible(true)}
									isAnimationActive={true}
									onMouseEnter={(_, index) => setActiveIndex(index)}
									onMouseLeave={() => setActiveIndex(null)}
									activeIndex={activeIndex}
									activeBar={({ ...props }) => {
										return (
											<Rectangle
												{...props}
												fillOpacity={0.7}
												strokeDasharray={4}
												strokeDashoffset={4}
											/>
										)
									}}
								/>
								<Bar
									dataKey="alto_risco_2"
									stackId="a"
									fill="#D12600"
									onClick={() => setIsTooltipVisible(true)}
									isAnimationActive={true}
									onMouseEnter={(_, index) => setActiveIndex(index)}
									onMouseLeave={() => setActiveIndex(null)}
									activeIndex={activeIndex}
									activeBar={({ ...props }) => {
										return (
											<Rectangle
												{...props}
												fillOpacity={0.7}
												strokeDasharray={4}
												strokeDashoffset={4}
											/>
										)
									}}

								/>
								<Bar
									dataKey="medio_risco"
									stackId="a"
									fill="#FFC530"
									onClick={() => setIsTooltipVisible(true)}
									isAnimationActive={true}
									onMouseEnter={(_, index) => setActiveIndex(index)}
									onMouseLeave={() => setActiveIndex(null)}
									activeIndex={activeIndex}
									activeBar={({ ...props }) => {
										return (
											<Rectangle
												{...props}
												fillOpacity={0.7}
												strokeDasharray={4}
												strokeDashoffset={4}
											/>
										)
									}}
								/>
								<Bar
									dataKey="baixo_risco"
									stackId="a"
									fill="#80F279"
									onClick={() => setIsTooltipVisible(true)}
									isAnimationActive={true}
									onMouseEnter={(_, index) => setActiveIndex(index)}
									onMouseLeave={() => setActiveIndex(null)}
									activeIndex={activeIndex}
									activeBar={({ ...props }) => {
										return (
											<Rectangle
												{...props}
												fillOpacity={0.7}
												strokeDasharray={4}
												strokeDashoffset={4}
											/>
										)
									}}
								/>
							</BarChart>
						</ChartContainer>
					</CardContent>
				:
					<div className={`flex w-full h-full justify-center`}>
						<EmptyComponent />
					</div>
			}
		</Card>
	);
				
};

export default StackedBarChart;
